/**
 * SignalChartDialog — the signal chart in a modal, for pages that have no room
 * for a split view (the live book's positions tab).
 *
 * Takes the same (screener, symbol, date) triple as the Signals page and
 * fetches through the same hook, so what it draws is the same engine replay.
 *
 * Provenance caveat it has to handle: a live position knows its symbol and
 * entry signal date, but not which screener opened it. The only screener on
 * hand is the book's *currently active* one, and a helm run rotates that pair
 * while older positions are still open — the same hazard migration 040 fixed
 * for trade configs by snapshotting them onto the position. So this component
 * checks whether the replayed screener actually passed on the entry bar, and
 * says so plainly when it did not, rather than presenting another screener's
 * indicators as the reason for the entry.
 */
import { AlertTriangle, RefreshCw } from 'lucide-react';

import SignalChart from './SignalChart';
import useIndicatorSeries from '../../hooks/useIndicatorSeries';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

export default function SignalChartDialog({
  open,
  onOpenChange,
  screenerId,
  screenerName,
  symbol,
  signalDate,
  subtitle,
  deps = false,
  onToggleDeps,
}) {
  // The hook no-ops until all three are present, so it is safe to call with a
  // closed dialog; gating on `open` keeps a closed dialog from replaying.
  const chart = useIndicatorSeries(
    open ? screenerId : null,
    open ? symbol : null,
    open ? signalDate : null,
    { deps }
  );

  const bare = (symbol || '').replace('.NS', '');
  const entry = chart.evaluations.find((e) => e.date === signalDate);
  // `passed === false` on the entry bar means this screener would not have
  // produced this entry. Absent evaluations (replay failed, or the date is not
  // a session) are not evidence either way, so they say nothing.
  const mismatched = entry?.passed === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(1100px,95vw)] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pr-8">
            <span className="text-lg font-bold">{bare}</span>
            {signalDate && (
              <span className="font-mono text-xs text-muted-foreground">
                {subtitle || `Signal ${signalDate}`}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {screenerName
              ? `Indicators from ${screenerName}, replayed over this symbol.`
              : 'Indicators replayed over this symbol.'}
          </DialogDescription>
        </DialogHeader>

        {!screenerId && (
          <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            This book has no active screener, so there are no indicators to
            plot. Run the helm to assign one.
          </p>
        )}

        {mismatched && (
          <p className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs leading-snug text-amber-600 dark:text-amber-400">
            <AlertTriangle size={14} className="mt-px shrink-0" />
            <span>
              {screenerName || 'The book’s current screener'} does not match on{' '}
              <span className="font-mono">{signalDate}</span>. A position opened
              before the book last rotated its screener was produced by a
              different one, so these indicators explain the symbol — not this
              entry.
            </span>
          </p>
        )}

        {screenerId && chart.loading && chart.ohlcv.length === 0 && (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground/50" />
            <p className="text-xs">Replaying {screenerName || 'screener'} over {bare}…</p>
          </div>
        )}

        {chart.ohlcv.length > 0 && (
          <SignalChart
            ohlcv={chart.ohlcv}
            series={chart.series}
            evaluations={chart.evaluations}
            blockers={chart.blockers}
            warnings={chart.warnings}
            error={chart.error}
            signalDate={signalDate}
            loading={chart.loading}
            deps={deps}
            onToggleDeps={onToggleDeps}
          />
        )}

        {screenerId && !chart.loading && chart.ohlcv.length === 0 && (
          <p className="py-10 text-center font-mono text-sm text-muted-foreground">
            [ {chart.error || 'No chart data available'} ]
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
